import React, { useState, useEffect } from 'react';
import { 
  FileText, CheckCircle, Monitor, ChevronRight, User, ShieldCheck, 
  ChevronLeft, Download, Eye, Trash2, UserCircle, CreditCard, 
  Briefcase, Key, FilePlus, HeartHandshake, X, Calendar, Filter, BarChart3, Users, Building2
} from 'lucide-react';
import { useLocation } from 'react-router-dom';

// --- STYLES DEFINITION ---
const styles = {
  label: "block text-[11px] font-bold text-slate-500 uppercase mb-1 tracking-tight font-['Roboto']",
  input: "w-full border border-slate-200 px-3 py-2 text-sm rounded focus:border-blue-500 outline-none transition-all font-['Poppins'] bg-white",
  section: "mb-8",
  header: "text-lg font-bold text-slate-800 mb-4 flex items-center gap-2 border-b pb-2",
  grid: "grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5",
  btnNext: "bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded font-bold text-sm flex items-center gap-2 transition-colors",
  btnBack: "bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-2 rounded font-bold text-sm flex items-center gap-2 transition-colors",
  tabActive: "border-b-2 border-blue-600 text-blue-600 pb-2 px-4 font-bold text-sm cursor-pointer",
  tabInactive: "text-slate-400 pb-2 px-4 font-bold text-sm cursor-pointer hover:text-slate-600"
};

// --- SUB-COMPONENTS ---
const InterviewFields = ({ data, updateFn }) => (
  <div className={styles.grid}>
    <div><label className={styles.label}>Interviewer Name</label><input className={styles.input} value={data.interviewer || ''} onChange={e => updateFn('interviewer', e.target.value)} /></div>
    <div><label className={styles.label}>Employee ID</label><input className={styles.input} value={data.empId || ''} onChange={e => updateFn('empId', e.target.value)} /></div>
    <div><label className={styles.label}>Email Address</label><input className={styles.input} value={data.email || ''} onChange={e => updateFn('email', e.target.value)} /></div>
    <div><label className={styles.label}>Date</label><input type="date" className={styles.input} value={data.date || ''} onChange={e => updateFn('date', e.target.value)} /></div>
    <div><label className={styles.label}>Time Slot</label><input type="time" className={styles.input} value={data.time || ''} onChange={e => updateFn('time', e.target.value)} /></div>
    <div><label className={styles.label}>Interview Mode</label>
      <select className={styles.input} value={data.mode || 'Face to Face'} onChange={e => updateFn('mode', e.target.value)}>
        <option>Face to Face</option>
        <option>Microsoft Teams</option>
        <option>Zoom Call</option>
      </select>
    </div>
  </div>
);

const InterviewLifecycleWorkspace = () => {
  const location = useLocation();
  const [view, setView] = useState('TRACKER'); 
  const [trackerTab, setTrackerTab] = useState('ACTIVE');
  const [currentStage, setCurrentStage] = useState('RECRUITMENT_INFO'); 
  const [showOfferPreview, setShowOfferPreview] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [reportType, setReportType] = useState('daily');
  const [skillFilter, setSkillFilter] = useState('');
  const [dateApplied, setDateApplied] = useState('');
  const [jobData, setJobData] = useState(null);
  
  useEffect(() => {
    if (location.state?.jobData) {
      setJobData(location.state.jobData);
      setView('WORKSPACE');
    }
  }, [location]);
  
  const [activeCandidates, setActiveCandidates] = useState([
    { id: 1, name: "Johnathan Doe", status: "ACTIVE", stage: "L1 - Basic", email: "j.doe@gmail.com", dateApplied: "2024-01-15", skills: "React, Node.js" }
  ]);
  const [holdCandidates, setHoldCandidates] = useState([]);
  const [rejectedCandidates, setRejectedCandidates] = useState([]);
  const [existingEmployees, setExistingEmployees] = useState([]);

  const [candidate, setCandidate] = useState({
    id: 1,
    name: "Johnathan Doe",
    resume: "Johnathan_Doe_Resume.pdf",
    userProfile: { 
      name: "Johnathan Doe", 
      email: "j.doe@gmail.com", 
      phone: "", 
      position: "", 
      experience: "", 
      skills: "",
      pan: "",
      aadhaar: "",
      dob: "",
      gender: "",
      location: "",
      noticePeriod: "",
      photo: null
    },
    l1: { interviewer: "", interviewerName: "", empId: "", email: "", date: "", time: "", mode: "Face to Face", score: "", feedback: "", meetingLink: "" },
    l2: { interviewer: "", empId: "", email: "", date: "", time: "", mode: "Microsoft Teams", feedback: "", techScore: "", techStack: "", meetingLink: "" },
    clientRound: { interviewer: "", empId: "", email: "", date: "", time: "", feedback: "", meetingLink: "" },
    l3: { interviewer: "", empId: "", email: "", date: "", time: "", mode: "Face to Face", cultureFit: "", expectedSalary: "", noticePeriod: "", reasonForChange: "" },
  });

  const [employee, setEmployee] = useState({
    empCode: `EMP-2025-099`,
    firstName: "Johnathan", middleName: "Alexander", lastName: "Doe",
    empType: "Permanent", doj: "2022-03-10", dob: "1994-06-22", gender: "Male",
    bloodGroup: "A+ Positive", proEmail: "john.doe@enterprise-ui.com", personalEmail: "j.doe.dev@gmail.com",
    status: "Active", fatherName: "Richard Doe", spouseName: "Mary Doe", spouseDob: "1995-01-12",
    children: "Alice Doe (5), Ben Doe (2)", emergencyContact: "Mary Doe", emergencyRelation: "Spouse",
    emergencyMobile: "+91 9988776655", vehicleNum: "TS-07-JK-1234", dl: "DL-KA01-2022",
    pan: "ABCDE1234Z", aadhaar: "XXXX-XXXX-1122", nationality: "Indian",
    currentAddress: "123, Tech Park Residency, Hyderabad", permanentAddress: "45/A, Green Valley, Bangalore",
    entity: "Global Systems Pvt Ltd", dept: "Product Development", manager: "Sarah Jenkins (SR-992)",
    location: "Hyderabad - HITEC City", grade: "L3", designation: "Software Architect",
    projectCode: "PROJ-OMEGA-25", groupDoj: "2022-03-10", lastWorkingDay: "N/A", resignationDate: "N/A",
    bankName: "HDFC Bank Ltd", accNum: "5010044556677", ifsc: "HDFC0000123",
    pfNo: "TS/HYD/12345/678", uan: "100998877665", esi: "NA", ppf: "9988772211",
    currentProject: "Cloud Infrastructure Modernization - Phase 2",
    assetName: "MacBook Pro M2", assetSerial: "SN-992833410", assetDate: "2022-03-12",
    outlookAccess: true, teamsAccess: true,
    policiesAssigned: ['Code of Conduct', 'IT Policy']
  });

  const [salary, setSalary] = useState({ 
    basic: 0, hra: 0, special: 0, lta: 0, pf: 0, variable: 0, total: 0, joinBonus: 0, relocation: 0 
  });

  const updateEmployee = (field, value) => {
    setEmployee(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    const total = Number(salary.basic) + Number(salary.hra) + Number(salary.special) + Number(salary.lta) + Number(salary.pf);
    setSalary(prev => ({ ...prev, total }));
  }, [salary.basic, salary.hra, salary.special, salary.lta, salary.pf]);

  const handleFinishOnboarding = () => {
    setExistingEmployees([...existingEmployees, { ...employee, salary: (salary.total * 12) }]);
    setActiveCandidates(activeCandidates.filter(c => c.id !== candidate.id));
    setView('TRACKER');
    downloadFullReport();
    alert("Onboarding Successful!");
  };

  const sendMeetingLink = (roundType, candidateEmail) => {
    const meetingLinks = {
      'screening': 'https://teams.microsoft.com/screening-interview',
      'first': 'https://teams.microsoft.com/first-round-interview', 
      'client': 'https://teams.microsoft.com/client-round-interview',
      'hr': 'https://teams.microsoft.com/hr-round-interview'
    };
    
    console.log(`Sending ${roundType} meeting link to ${candidateEmail}:`, meetingLinks[roundType]);
    alert(`Meeting link sent to ${candidateEmail} for ${roundType} round`);
  };

  const validateCurrentStage = () => {
    switch (currentStage) {
      case 'RECRUITMENT_INFO':
        // No validation needed for recruitment info, just display
        return true;
      case 'USER_PROFILE':
        const { name, email, phone, position, experience, skills, pan, aadhaar, dob, gender, location, noticePeriod, photo } = candidate.userProfile;
        if (!name || !email || !phone || !position || !experience || !skills || !pan || !aadhaar || !dob || !gender || !location || !noticePeriod || !photo) {
          alert('Please fill all User Profile fields before proceeding.');
          return false;
        }
        break;
      case 'L1_EVAL':
        const { interviewerName, empId, date, time, meetingLink } = candidate.l1;
        if (!interviewerName || !empId || !date || !time || !meetingLink) {
          alert('Please fill all Screening fields before proceeding.');
          return false;
        }
        break;
      case 'L2_EVAL':
        const l2 = candidate.l2;
        if (!l2.interviewer || !l2.empId || !l2.email || !l2.date || !l2.time || !l2.meetingLink || !l2.techStack || !l2.techScore) {
          alert('Please fill all First Round fields before proceeding.');
          return false;
        }
        break;
      case 'CLIENT_ROUND':
        const client = candidate.clientRound;
        if (!client.interviewer || !client.empId || !client.email || !client.date || !client.time || !client.meetingLink) {
          alert('Please fill all Client Round fields before proceeding.');
          return false;
        }
        break;
      case 'L3_EVAL':
        const l3 = candidate.l3;
        if (!l3.interviewer || !l3.empId || !l3.email || !l3.date || !l3.time || !l3.expectedSalary || !l3.noticePeriod || !l3.cultureFit || !l3.reasonForChange) {
          alert('Please fill all HR Round fields before proceeding.');
          return false;
        }
        break;
      case 'OFFER':
        if (!salary.basic || !salary.hra || !salary.special || !salary.pf) {
          alert('Please fill all Offer Configuration fields before proceeding.');
          return false;
        }
        break;
    }
    return true;
  };

  const handleMoveForward = () => {
    if (!validateCurrentStage()) {
      return;
    }
    
    const stages = ['RECRUITMENT_INFO', 'USER_PROFILE', 'L1_EVAL', 'L2_EVAL', 'CLIENT_ROUND', 'L3_EVAL', 'OFFER', 'ONBOARDING'];
    const idx = stages.indexOf(currentStage);
    
    // Send meeting links when moving to interview rounds
    if (currentStage === 'L1_EVAL') {
      sendMeetingLink('first', candidate.userProfile.email);
    } else if (currentStage === 'L2_EVAL') {
      sendMeetingLink('client', candidate.userProfile.email);
    } else if (currentStage === 'CLIENT_ROUND') {
      sendMeetingLink('hr', candidate.userProfile.email);
    }
    
    if (idx < stages.length - 1) setCurrentStage(stages[idx + 1]);
  };

  const downloadReport = (type) => {
    const reportData = { type, date: new Date().toISOString(), candidates: activeCandidates };
    console.log(`Downloading ${type} report:`, reportData);
    alert(`${type.charAt(0).toUpperCase() + type.slice(1)} report downloaded as PDF`);
  };

  const downloadFullReport = () => {
    const fullReport = {
      candidate: candidate.name,
      stages: {
        userProfile: candidate.userProfile,
        screening: candidate.l1,
        firstRound: candidate.l2,
        clientRound: candidate.clientRound,
        hrRound: candidate.l3,
        onboarding: employee
      },
      completedDate: new Date().toISOString()
    };
    console.log('Full candidate report:', fullReport);
    alert('Complete candidate journey report downloaded as PDF');
  };

  const filteredCandidates = (candidates) => {
    return candidates.filter(candidate => 
      !skillFilter || candidate.skills?.toLowerCase().includes(skillFilter.toLowerCase())
    );
  };

  const handleHold = () => {
    setHoldCandidates([...holdCandidates, { ...candidate, status: 'HOLD' }]);
    setActiveCandidates(activeCandidates.filter(c => c.id !== candidate.id));
    setView('TRACKER');
  };

  const handleReject = () => {
    setRejectedCandidates([...rejectedCandidates, { ...candidate, status: 'REJECTED' }]);
    setActiveCandidates(activeCandidates.filter(c => c.id !== candidate.id));
    setView('TRACKER');
  };

  return (
    <div className="min-h-screen bg-[#fcfcfc] font-['Poppins']">
      <nav className="h-16 bg-white border-b px-8 flex items-center justify-between sticky top-0 z-50 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="text-blue-700 font-black text-2xl tracking-tighter">G-ERP</div>
          <div className="h-6 w-[1px] bg-slate-200"></div>
          <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Recruitment & Onboarding</p>
        </div>
        <div className="flex gap-6">
          <button onClick={() => setView('TRACKER')} className="text-slate-500 hover:text-blue-600 font-bold text-sm">Dashboard</button>
        </div>
      </nav>

      <div className="p-8 max-w-[1600px] mx-auto">
        {view === 'TRACKER' ? (
          <div className="animate-in fade-in">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-2xl font-bold italic">Process Overview</h1>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-slate-500"/>
                  <input type="date" className={styles.input + " w-40"} value={dateApplied} onChange={e => setDateApplied(e.target.value)} placeholder="Date Applied"/>
                </div>
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-slate-500"/>
                  <input className={styles.input + " w-32"} value={skillFilter} onChange={e => setSkillFilter(e.target.value)} placeholder="Filter by skills"/>
                </div>
                <div className="flex items-center gap-2">
                  <BarChart3 size={16} className="text-slate-500"/>
                  <select className={styles.input + " w-32"} value={reportType} onChange={e => setReportType(e.target.value)}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="halfyearly">Half Yearly</option>
                    <option value="yearly">Yearly</option>
                    <option value="custom">Custom</option>
                  </select>
                  <button onClick={() => downloadReport(reportType)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-xs font-bold flex items-center gap-1">
                    <Download size={14}/> PDF
                  </button>
                </div>
                <div className="flex border rounded-lg bg-white p-1">
                  <button onClick={() => setTrackerTab('ACTIVE')} className={trackerTab === 'ACTIVE' ? styles.tabActive : styles.tabInactive}>Active</button>
                  <button onClick={() => setTrackerTab('HOLD')} className={trackerTab === 'HOLD' ? styles.tabActive : styles.tabInactive}>On Hold</button>
                  <button onClick={() => setTrackerTab('REJECTED')} className={trackerTab === 'REJECTED' ? styles.tabActive : styles.tabInactive}>Rejected</button>
                </div>
              </div>
            </div>

            <table className="w-full bg-white border rounded-xl overflow-hidden shadow-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-[11px] font-bold text-slate-500 uppercase">
                  <th className="p-4">Name</th>
                  <th className="p-4">Date Applied</th>
                  <th className="p-4">Skills</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Current Stage</th>
                  <th className="p-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredCandidates(trackerTab === 'ACTIVE' ? activeCandidates : trackerTab === 'HOLD' ? holdCandidates : rejectedCandidates).map((item, idx) => (
                  <tr key={idx} className="border-b hover:bg-slate-50 transition-colors">
                    <td className="p-4 font-bold text-slate-700">{item.name}</td>
                    <td className="p-4 text-sm text-slate-600">{item.dateApplied || 'N/A'}</td>
                    <td className="p-4 text-sm text-slate-600">{item.skills || 'N/A'}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-[10px] font-bold ${item.status === 'REJECTED' ? 'bg-red-100 text-red-700' : item.status === 'HOLD' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm font-medium">{item.stage}</td>
                    <td className="p-4">
                      {trackerTab === 'ACTIVE' && (
                        <button onClick={() => { setCandidate({...candidate, name: item.name}); setView('WORKSPACE'); }} className="text-blue-600 font-bold text-sm hover:underline">Manage Candidate</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex gap-8">
            {/* WIZARD SIDEBAR */}
            <div className="w-72 shrink-0">
                <div className="sticky top-24 space-y-2">
                  {['Job Requisition Details', 'User Profile', 'Screening', 'First Round', 'Client Round', 'HR Round', 'OFFER Letter', 'ONBOARDING'].map((s, idx) => {
                    const stageMap = {
                      'Job Requisition Details': 'RECRUITMENT_INFO',
                      'User Profile': 'USER_PROFILE',
                      'Screening': 'L1_EVAL',
                      'First Round': 'L2_EVAL',
                      'Client Round': 'CLIENT_ROUND',
                      'HR Round': 'L3_EVAL',
                      'OFFER Letter': 'OFFER',
                      'ONBOARDING': 'ONBOARDING'
                    };
                    return (
                      <div key={s} className={`p-4 rounded-lg flex items-center gap-3 transition-all ${currentStage === stageMap[s] ? 'bg-blue-700 text-white shadow-md translate-x-2' : 'bg-white text-slate-400 border border-slate-200 opacity-60'}`}>
                         <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] border ${currentStage === stageMap[s] ? 'bg-blue-600 border-blue-400' : 'bg-slate-100'}`}>{idx+1}</span>
                         <span className="text-sm font-bold">{s.replace('_', ' ')}</span>
                      </div>
                    );
                  })}
                </div>
            </div>

            <div className="flex-1 pb-32">
              {/* FIXED VIEW RESUME SECTION - Explicitly styled for visibility */}
              <div className="bg-white border-2 border-blue-100 p-4 rounded-xl flex items-center justify-between mb-8 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <FileText className="text-blue-600" size={24}/>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-bold text-slate-400">Candidate Resume</p>
                    <p className="text-sm font-bold text-slate-800">{candidate.resume}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowResumeModal(true)} 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 shadow-sm transition-all"
                >
                  <Eye size={16}/> VIEW RESUME
                </button>
              </div>

              {/* RECRUITMENT INFO */}
              {currentStage === 'RECRUITMENT_INFO' && (
                <div className="animate-in slide-in-from-right-4">
                  <div className={styles.header}><Building2 size={18}/> Recruitment Information</div>
                  
                  <div className="mb-8 p-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border border-indigo-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-lg font-bold text-slate-800 mb-4">Job Requisition Details</h3>
                        <div className="space-y-3">
                          <div>
                            <label className={styles.label}>REQ ID</label>
                            <div className="text-lg font-bold text-indigo-600">{jobData?.reqId || 'REQ-2024-001'}</div>
                          </div>
                          <div>
                            <label className={styles.label}>Job Title</label>
                            <div className="text-sm font-medium text-slate-700">{jobData?.title || 'Lead Software Engineer'}</div>
                          </div>
                          <div>
                            <label className={styles.label}>Department</label>
                            <div className="text-sm text-slate-600">{jobData?.department || 'Technology'}</div>
                          </div>
                          <div>
                            <label className={styles.label}>Positions Available</label>
                            <div className="text-sm text-slate-600">{jobData?.positions || '2'}</div>
                          </div>
                          {jobData?.description && (
                            <div>
                              <label className={styles.label}>Job Description</label>
                              <div className="text-sm text-slate-600">{jobData.description}</div>
                            </div>
                          )}
                          {jobData?.attachingLink && (
                            <div>
                              <label className={styles.label}>Attaching Link</label>
                              <a href={jobData.attachingLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                                {jobData.attachingLink}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-bold text-slate-800 mb-4">Interview Process</h3>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                            <span className="text-sm text-slate-600">Screening Round (L1)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                            <span className="text-sm text-slate-600">Technical Round (L2)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                            <span className="text-sm text-slate-600">Client Round</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                            <span className="text-sm text-slate-600">HR Round (L3)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                            <span className="text-sm text-slate-600">Offer & Onboarding</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700 font-medium flex items-center gap-2">
                      <ShieldCheck size={16} />
                      Ready to begin the interview process for this requisition. Click "MOVE FORWARD" to proceed to candidate profile setup.
                    </p>
                  </div>
                </div>
              )}

              {/* USER PROFILE */}
              {currentStage === 'USER_PROFILE' && (
                <div className="animate-in slide-in-from-right-4">
                  <div className={styles.header}><User size={18}/> User Profile Information</div>
                  
                  {/* Photo Upload Section */}
                  <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                    <div className="flex items-center gap-6">
                      <div className="relative">
                        <div className="w-32 h-32 bg-slate-200 rounded-full border-4 border-white shadow-lg flex items-center justify-center overflow-hidden">
                          {candidate.userProfile.photo ? (
                            <img src={URL.createObjectURL(candidate.userProfile.photo)} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                            <User size={48} className="text-slate-400" />
                          )}
                        </div>
                        <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
                          <input type="file" accept="image/*" className="hidden" onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, photo: e.target.files[0]}})} required />
                          <FilePlus size={16} />
                        </label>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-800 mb-1">Profile Photo *</h3>
                        <p className="text-sm text-slate-600">Upload a professional photo (JPG, PNG)</p>
                        <p className="text-xs text-red-500 mt-1">* Required field</p>
                      </div>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className={styles.grid}>
                    <div><label className={styles.label}>Full Name *</label><input className={styles.input} value={candidate.userProfile.name} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, name: e.target.value}})} required /></div>
                    <div><label className={styles.label}>Email Address *</label><input className={styles.input} type="email" value={candidate.userProfile.email} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, email: e.target.value}})} required /></div>
                    <div><label className={styles.label}>Phone Number *</label><input className={styles.input} value={candidate.userProfile.phone} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, phone: e.target.value}})} required /></div>
                    <div><label className={styles.label}>Applied Position *</label><input className={styles.input} value={candidate.userProfile.position} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, position: e.target.value}})} required /></div>
                    <div><label className={styles.label}>Years of Experience *</label><input className={styles.input} type="number" value={candidate.userProfile.experience} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, experience: e.target.value}})} required /></div>
                    <div><label className={styles.label}>Key Skills *</label><input className={styles.input} value={candidate.userProfile.skills} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, skills: e.target.value}})} placeholder="e.g. React, Node.js, Python" required /></div>
                    <div><label className={styles.label}>PAN Number *</label><input className={styles.input} value={candidate.userProfile.pan} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, pan: e.target.value}})} placeholder="ABCDE1234F" pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}" required /></div>
                    <div><label className={styles.label}>Aadhaar Number *</label><input className={styles.input} value={candidate.userProfile.aadhaar} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, aadhaar: e.target.value}})} placeholder="1234-5678-9012" pattern="[0-9]{4}-[0-9]{4}-[0-9]{4}" required /></div>
                    <div><label className={styles.label}>Date of Birth *</label><input className={styles.input} type="date" value={candidate.userProfile.dob} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, dob: e.target.value}})} required /></div>
                    <div><label className={styles.label}>Gender *</label><select className={styles.input} value={candidate.userProfile.gender} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, gender: e.target.value}})} required><option value="">Select Gender</option><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option></select></div>
                    <div><label className={styles.label}>Current Location *</label><input className={styles.input} value={candidate.userProfile.location} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, location: e.target.value}})} required /></div>
                    <div><label className={styles.label}>Notice Period (Days) *</label><input className={styles.input} type="number" value={candidate.userProfile.noticePeriod} onChange={e => setCandidate({...candidate, userProfile: {...candidate.userProfile, noticePeriod: e.target.value}})} required /></div>
                  </div>
                  
                  {/* Validation Message */}
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700 font-medium flex items-center gap-2">
                      <ShieldCheck size={16} />
                      All fields marked with (*) are mandatory and must be completed before proceeding.
                    </p>
                  </div>
                </div>
              )}

              {/* L1 EVALUATION (SCREENING) */}
              {currentStage === 'L1_EVAL' && (
                <div className="animate-in slide-in-from-right-4">
                  <div className={styles.header}><UserCircle size={18}/> Screening Round - L1</div>
                  <div className={styles.grid}>
                    <div><label className={styles.label}>Interviewer Name</label><input className={styles.input} value={candidate.l1.interviewerName} onChange={e => setCandidate({...candidate, l1: {...candidate.l1, interviewerName: e.target.value}})} /></div>
                    <div><label className={styles.label}>Employee ID</label><input className={styles.input} value={candidate.l1.empId} onChange={e => setCandidate({...candidate, l1: {...candidate.l1, empId: e.target.value}})} /></div>
                    <div><label className={styles.label}>Interview Date</label><input type="date" className={styles.input} value={candidate.l1.date} onChange={e => setCandidate({...candidate, l1: {...candidate.l1, date: e.target.value}})} /></div>
                    <div><label className={styles.label}>Time Slot</label><input type="time" className={styles.input} value={candidate.l1.time} onChange={e => setCandidate({...candidate, l1: {...candidate.l1, time: e.target.value}})} /></div>
                    <div><label className={styles.label}>Meeting Link</label><input className={styles.input} value={candidate.l1.meetingLink} onChange={e => setCandidate({...candidate, l1: {...candidate.l1, meetingLink: e.target.value}})} placeholder="https://teams.microsoft.com/..."/></div>
                  </div>
                </div>
              )}

              {/* L2 EVALUATION (TECHNICAL) */}
              {currentStage === 'L2_EVAL' && (
                <div className="animate-in slide-in-from-right-4">
                  <div className={styles.header}><Monitor size={18}/> L2 - Technical Assessment Round</div>
                  <InterviewFields data={candidate.l2} updateFn={(f, v) => setCandidate({...candidate, l2: {...candidate.l2, [f]: v}})} />
                  <div className="mt-6 grid grid-cols-3 gap-4">
                      <div><label className={styles.label}>Core Tech Stack Proficiency</label><input className={styles.input} value={candidate.l2.techStack} onChange={e => setCandidate({...candidate, l2: {...candidate.l2, techStack: e.target.value}})} placeholder="e.g. React, Node.js, AWS"/></div>
                      <div><label className={styles.label}>Technical Score (1-10)</label><input type="number" className={styles.input} value={candidate.l2.techScore} onChange={e => setCandidate({...candidate, l2: {...candidate.l2, techScore: e.target.value}})} /></div>
                      <div><label className={styles.label}>Meeting Link</label><input className={styles.input} value={candidate.l2.meetingLink} onChange={e => setCandidate({...candidate, l2: {...candidate.l2, meetingLink: e.target.value}})} placeholder="https://teams.microsoft.com/..."/></div>
                  </div>
                </div>
              )}

              {/* CLIENT ROUND */}
              {currentStage === 'CLIENT_ROUND' && (
                <div className="animate-in slide-in-from-right-4">
                  <div className={styles.header}><Users size={18}/> Client Round</div>
                  <div className={styles.grid}>
                    <div><label className={styles.label}>Interviewer Name</label><input className={styles.input} value={candidate.clientRound.interviewer} onChange={e => setCandidate({...candidate, clientRound: {...candidate.clientRound, interviewer: e.target.value}})} /></div>
                    <div><label className={styles.label}>Employee ID</label><input className={styles.input} value={candidate.clientRound.empId} onChange={e => setCandidate({...candidate, clientRound: {...candidate.clientRound, empId: e.target.value}})} /></div>
                    <div><label className={styles.label}>Email Address</label><input className={styles.input} value={candidate.clientRound.email} onChange={e => setCandidate({...candidate, clientRound: {...candidate.clientRound, email: e.target.value}})} /></div>
                    <div><label className={styles.label}>Date</label><input type="date" className={styles.input} value={candidate.clientRound.date} onChange={e => setCandidate({...candidate, clientRound: {...candidate.clientRound, date: e.target.value}})} /></div>
                    <div><label className={styles.label}>Time Slot</label><input type="time" className={styles.input} value={candidate.clientRound.time} onChange={e => setCandidate({...candidate, clientRound: {...candidate.clientRound, time: e.target.value}})} /></div>
                    <div><label className={styles.label}>Meeting Link</label><input className={styles.input} value={candidate.clientRound.meetingLink} onChange={e => setCandidate({...candidate, clientRound: {...candidate.clientRound, meetingLink: e.target.value}})} placeholder="https://teams.microsoft.com/..."/></div>
                  </div>
                </div>
              )}

              {/* L3 EVALUATION (HR ROUND) */}
              {currentStage === 'L3_EVAL' && (
                <div className="animate-in slide-in-from-right-4">
                  <div className={styles.header}><HeartHandshake size={18}/> L3 - HR & Culture Fit Round</div>
                  <InterviewFields data={candidate.l3} updateFn={(f, v) => setCandidate({...candidate, l3: {...candidate.l3, [f]: v}})} />
                  <div className="mt-6 grid grid-cols-3 gap-4">
                      <div><label className={styles.label}>Expected Salary (LPA)</label><input className={styles.input} value={candidate.l3.expectedSalary} onChange={e => setCandidate({...candidate, l3: {...candidate.l3, expectedSalary: e.target.value}})} /></div>
                      <div><label className={styles.label}>Notice Period (Days)</label><input className={styles.input} value={candidate.l3.noticePeriod} onChange={e => setCandidate({...candidate, l3: {...candidate.l3, noticePeriod: e.target.value}})} /></div>
                      <div><label className={styles.label}>Culture Fit Score (1-10)</label><input type="number" className={styles.input} value={candidate.l3.cultureFit} onChange={e => setCandidate({...candidate, l3: {...candidate.l3, cultureFit: e.target.value}})} /></div>
                  </div>
                  <div className="mt-6"><label className={styles.label}>Reason for Leaving / HR Remarks</label><textarea className={styles.input + " h-32"} value={candidate.l3.reasonForChange} onChange={e => setCandidate({...candidate, l3: {...candidate.l3, reasonForChange: e.target.value}})} /></div>
                </div>
              )}

              {/* OFFER CONFIGURATION - Expanded Professional Fields */}
              {currentStage === 'OFFER' && (
                <div className="animate-in slide-in-from-right-4">
                  <div className={styles.header}><Briefcase size={18}/> Professional Offer Configuration</div>
                  <div className={styles.grid}>
                    <div><label className={styles.label}>Monthly Basic</label><input type="number" className={styles.input} onChange={e => setSalary({...salary, basic: e.target.value})} /></div>
                    <div><label className={styles.label}>HRA (40% of Basic)</label><input type="number" className={styles.input} onChange={e => setSalary({...salary, hra: e.target.value})} /></div>
                    <div><label className={styles.label}>Special Allowance</label><input type="number" className={styles.input} onChange={e => setSalary({...salary, special: e.target.value})} /></div>
                    <div><label className={styles.label}>Employer PF</label><input type="number" className={styles.input} onChange={e => setSalary({...salary, pf: e.target.value})} /></div>
                    <div><label className={styles.label}>Annual Variable (%)</label><input type="number" className={styles.input} onChange={e => setSalary({...salary, variable: e.target.value})} /></div>
                    <div><label className={styles.label}>Joining Bonus (One-time)</label><input type="number" className={styles.input} onChange={e => setSalary({...salary, joinBonus: e.target.value})} /></div>
                    <div><label className={styles.label}>Relocation Reimbursement</label><input type="number" className={styles.input} onChange={e => setSalary({...salary, relocation: e.target.value})} /></div>
                    <div><label className={styles.label}>Offer Expiry Date</label><input type="date" className={styles.input} /></div>
                  </div>
                  
                  <div className="mt-10 p-8 bg-slate-900 rounded-2xl flex justify-between items-center text-white shadow-xl border-4 border-blue-500/20">
                      <div>
                        <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Total Annual Gross CTC (INR)</p>
                        <p className="text-4xl font-black text-blue-400">₹ {(salary.total * 12).toLocaleString('en-IN')}</p>
                      </div>
                      <div className="flex gap-4">
                        <button onClick={() => setShowOfferPreview(true)} className="bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl flex items-center gap-2 text-sm font-bold border border-white/20 transition-all">
                          <Download size={18}/> Generate Letter
                        </button>
                        <button onClick={() => setShowOfferPreview(true)} className="bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-xl flex items-center gap-2 text-sm font-bold shadow-lg transition-all">
                          <Eye size={18}/> Preview Offer
                        </button>
                      </div>
                  </div>
                </div>
              )}

              {/* ONBOARDING MASTER DATA */}
              {currentStage === 'ONBOARDING' && (
                <div className="space-y-12 animate-in fade-in">
                  <section>
                    <div className={styles.header}><User size={18}/> Personal & Employment Details</div>
                    <div className={styles.grid}>
                      <div><label className={styles.label}>Employee Code</label><input className={styles.input + " bg-slate-50 font-bold"} value={employee.empCode} readOnly /></div>
                      <div><label className={styles.label}>First Name *</label><input className={styles.input} value={employee.firstName} onChange={e => updateEmployee('firstName', e.target.value)} required /></div>
                      <div><label className={styles.label}>Middle Name *</label><input className={styles.input} value={employee.middleName} onChange={e => updateEmployee('middleName', e.target.value)} required /></div>
                      <div><label className={styles.label}>Last Name *</label><input className={styles.input} value={employee.lastName} onChange={e => updateEmployee('lastName', e.target.value)} required /></div>
                      <div><label className={styles.label}>Employment Type *</label><input className={styles.input} value={employee.empType} onChange={e => updateEmployee('empType', e.target.value)} required /></div>
                      <div><label className={styles.label}>Date of Joining *</label><input type="date" className={styles.input} value={employee.doj} onChange={e => updateEmployee('doj', e.target.value)} required /></div>
                      <div><label className={styles.label}>Date of Birth *</label><input type="date" className={styles.input} value={employee.dob} onChange={e => updateEmployee('dob', e.target.value)} required /></div>
                      <div><label className={styles.label}>Gender *</label><input className={styles.input} value={employee.gender} onChange={e => updateEmployee('gender', e.target.value)} required /></div>
                      <div><label className={styles.label}>Blood Group *</label><input className={styles.input} value={employee.bloodGroup} onChange={e => updateEmployee('bloodGroup', e.target.value)} required /></div>
                      <div><label className={styles.label}>Professional Email *</label><input className={styles.input} value={employee.proEmail} onChange={e => updateEmployee('proEmail', e.target.value)} required /></div>
                      <div><label className={styles.label}>Personal Email *</label><input className={styles.input} value={employee.personalEmail} onChange={e => updateEmployee('personalEmail', e.target.value)} required /></div>
                      <div><label className={styles.label}>Employment Status *</label><input className={styles.input} value={employee.status} onChange={e => updateEmployee('status', e.target.value)} required /></div>
                      <div><label className={styles.label}>PAN Number *</label><input className={styles.input} value={employee.pan} onChange={e => updateEmployee('pan', e.target.value)} required /></div>
                      <div><label className={styles.label}>Aadhaar Number *</label><input className={styles.input} value={employee.aadhaar} onChange={e => updateEmployee('aadhaar', e.target.value)} required /></div>
                      <div className="col-span-2"><label className={styles.label}>Photo *</label><input className={styles.input} type="file" accept="image/*" onChange={e => updateEmployee('photo', e.target.files[0])} required /></div>
                    </div>
                  </section>

                  <section>
                    <div className={styles.header}>Family & Emergency</div>
                    <div className={styles.grid}>
                      <div><label className={styles.label}>Father's Name *</label><input className={styles.input} value={employee.fatherName} onChange={e => updateEmployee('fatherName', e.target.value)} required /></div>
                      <div><label className={styles.label}>Spouse Name *</label><input className={styles.input} value={employee.spouseName} onChange={e => updateEmployee('spouseName', e.target.value)} required /></div>
                      <div><label className={styles.label}>Spouse DOB *</label><input type="date" className={styles.input} value={employee.spouseDob} onChange={e => updateEmployee('spouseDob', e.target.value)} required /></div>
                      <div><label className={styles.label}>Children Info *</label><input className={styles.input} value={employee.children} onChange={e => updateEmployee('children', e.target.value)} required /></div>
                      <div><label className={styles.label}>Emergency Contact *</label><input className={styles.input} value={employee.emergencyContact} onChange={e => updateEmployee('emergencyContact', e.target.value)} required /></div>
                      <div><label className={styles.label}>Relation *</label><input className={styles.input} value={employee.emergencyRelation} onChange={e => updateEmployee('emergencyRelation', e.target.value)} required /></div>
                      <div><label className={styles.label}>Emergency Mobile *</label><input className={styles.input} value={employee.emergencyMobile} onChange={e => updateEmployee('emergencyMobile', e.target.value)} required /></div>
                    </div>
                  </section>

                  <section>
                    <div className={styles.header}>Legal & Residential</div>
                    <div className={styles.grid}>
                      <div><label className={styles.label}>Driving License *</label><input className={styles.input} value={employee.dl} onChange={e => updateEmployee('dl', e.target.value)} required /></div>
                      <div><label className={styles.label}>Vehicle Number *</label><input className={styles.input} value={employee.vehicleNum} onChange={e => updateEmployee('vehicleNum', e.target.value)} required /></div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-5">
                      <div><label className={styles.label}>Current Address *</label><textarea className={styles.input} value={employee.currentAddress} onChange={e => updateEmployee('currentAddress', e.target.value)} required /></div>
                      <div><label className={styles.label}>Permanent Address *</label><textarea className={styles.input} value={employee.permanentAddress} onChange={e => updateEmployee('permanentAddress', e.target.value)} required /></div>
                    </div>
                  </section>

                  <section>
                    <div className={styles.header}>Workspace, Asset & Banking</div>
                    <div className={styles.grid}>
                      <div><label className={styles.label}>Department *</label><input className={styles.input} value={employee.dept} required /></div>
                      <div><label className={styles.label}>Manager *</label><input className={styles.input} value={employee.manager} required /></div>
                      <div><label className={styles.label}>Designation *</label><input className={styles.input} value={employee.designation} required /></div>
                      <div><label className={styles.label}>Asset Assigned *</label><input className={styles.input} value={employee.assetName} required /></div>
                      <div><label className={styles.label}>Bank Name *</label><input className={styles.input} value={employee.bankName} onChange={e => updateEmployee('bankName', e.target.value)} required /></div>
                      <div><label className={styles.label}>Account Number *</label><input className={styles.input} value={employee.accNum} onChange={e => updateEmployee('accNum', e.target.value)} required /></div>
                      <div><label className={styles.label}>IFSC Code *</label><input className={styles.input} value={employee.ifsc} onChange={e => updateEmployee('ifsc', e.target.value)} required /></div>
                    </div>
                  </section>

                  <section className="bg-slate-50 p-6 rounded-lg border border-dashed border-slate-300">
                    <div className={styles.header}><Key size={18}/> Access Provisioning & Policies</div>
                    <div className={styles.grid}>
                       <div className="flex items-center gap-2">
                         <input type="checkbox" className="w-4 h-4" checked={employee.outlookAccess} onChange={e => updateEmployee('outlookAccess', e.target.checked)} />
                         <label className="text-sm font-bold">Outlook Office 365</label>
                       </div>
                       <div className="flex items-center gap-2">
                         <input type="checkbox" className="w-4 h-4" checked={employee.teamsAccess} onChange={e => updateEmployee('teamsAccess', e.target.checked)} />
                         <label className="text-sm font-bold">Microsoft Teams</label>
                       </div>
                       <div>
                         <label className={styles.label}>Assign Policies</label>
                         <select multiple className={styles.input + " h-20"}>
                           <option>Code of Conduct</option>
                           <option>Data Security Policy</option>
                           <option>Hybrid Work Policy</option>
                         </select>
                       </div>
                    </div>
                  </section>

                  <div className="flex gap-4">
                    <button onClick={handleFinishOnboarding} className="flex-1 bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 shadow-lg transition-all">
                      <CheckCircle size={20}/> COMPLETE ONBOARDING & GENERATE EMP ID
                    </button>
                    <button onClick={downloadFullReport} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg transition-all">
                      <Download size={20}/> DOWNLOAD FULL REPORT
                    </button>
                  </div>
                </div>
              )}

              {/* NAVIGATION FOOTER */}
              <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex justify-between items-center shadow-[0_-4px_20px_rgba(0,0,0,0.05)] px-12 z-40">
                <button onClick={() => {
                  const stages = ['RECRUITMENT_INFO', 'USER_PROFILE', 'L1_EVAL', 'L2_EVAL', 'CLIENT_ROUND', 'L3_EVAL', 'OFFER', 'ONBOARDING'];
                  const idx = stages.indexOf(currentStage);
                  if (idx > 0) setCurrentStage(stages[idx - 1]);
                  else setView('TRACKER');
                }} className={styles.btnBack}>
                  <ChevronLeft size={18}/> BACK
                </button>
                
                {currentStage !== 'ONBOARDING' && (
                  <div className="flex gap-4 items-center">
                    <button onClick={handleHold} className="text-sm font-bold text-amber-600 hover:bg-amber-50 px-4 py-2 rounded-lg">Move to Hold</button>
                    <button onClick={handleReject} className="text-sm font-bold text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg">Reject Candidate</button>
                    <button onClick={handleMoveForward} className={styles.btnNext}>
                      MOVE FORWARD <ChevronRight size={18}/>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* MODAL: RESUME VIEW (Enhanced UI) */}
      {showResumeModal && (
        <div className="fixed inset-0 bg-slate-900/90 z-[200] flex items-center justify-center p-8 backdrop-blur-sm">
            <div className="bg-white w-full max-w-5xl h-full rounded-2xl overflow-hidden flex flex-col shadow-2xl">
                <div className="p-4 border-b flex justify-between items-center bg-slate-50">
                    <h3 className="font-bold flex items-center gap-3 text-slate-800">
                      <div className="bg-blue-600 p-1.5 rounded-lg text-white"><FileText size={18}/></div>
                      Candidate Portfolio: {candidate.name}
                    </h3>
                    <button onClick={() => setShowResumeModal(false)} className="p-2 hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors rounded-full">
                      <X size={24}/>
                    </button>
                </div>
                <div className="flex-1 bg-slate-200 flex items-center justify-center relative">
                    <div className="text-slate-400 font-bold flex flex-col items-center gap-2">
                       <FileText size={64} className="opacity-20"/>
                       <p>PDF RENDERER ENGINE ACTIVE</p>
                       <p className="text-[10px] uppercase tracking-widest">{candidate.resume}</p>
                    </div>
                </div>
                <div className="p-4 bg-slate-50 border-t flex justify-end gap-3">
                   <button onClick={() => setShowResumeModal(false)} className="px-6 py-2 text-sm font-bold text-slate-600">Close Preview</button>
                   <button className="bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-bold flex items-center gap-2">
                     <Download size={16}/> Download PDF
                   </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default InterviewLifecycleWorkspace;